/**
 * Represents the trend direction for candlestick data points.
 * Used across the application for audio palette selection and data representation.
 */
export type CandlestickTrend = 'Bull' | 'Bear' | 'Neutral';

/**
 * Format function signature for axis values.
 * Takes a value (number or string) and returns a formatted string.
 *
 * @example
 * // Currency formatting
 * const currencyFormat: FormatFunction = (v) => `$${Number(v).toFixed(2)}`;
 *
 * @example
 * // Date formatting
 * const dateFormat: FormatFunction = (v) => new Date(v).toLocaleDateString();
 */
export type FormatFunction = (value: number | string) => string;

/**
 * Supported format type specifiers for JSON/HTML API.
 */
export type FormatType = 'currency' | 'percent' | 'fixed' | 'number' | 'date' | 'scientific';

/**
 * Configuration for formatting values on an axis.
 *
 * Two ways to specify formatting:
 * 1. `function` - Function body string (for custom logic)
 * 2. `type` - Format type specifier (for common patterns)
 *
 * @example
 * // Using function string
 * { "function": "return `$${Number(value).toFixed(2)}`" }
 *
 * @example
 * // Using type specifier
 * { "type": "currency", "decimals": 2 }
 */
export interface AxisFormat {
  /**
   * Function body string for custom formatting.
   * The function receives `value` as parameter and must return a string.
   *
   * @example
   * // Currency formatting
   * { "function": "return `$${Number(value).toFixed(2)}`" }
   *
   * @example
   * // Date formatting
   * { "function": "return new Date(value).toLocaleDateString('en-US')" }
   */
  function?: string;

  /**
   * Format type specifier for common formatting patterns.
   * Use with `decimals`, `currency`, `locale`, `dateOptions` for customization.
   *
   * @example
   * { "type": "currency", "currency": "USD", "decimals": 2 }
   * { "type": "percent", "decimals": 1 }
   * { "type": "date", "dateOptions": { "month": "short", "day": "numeric" } }
   */
  type?: FormatType;

  /**
   * Number of decimal places for numeric formatters.
   * Used with: currency, percent, fixed, number, scientific
   * @default varies by type
   */
  decimals?: number;

  /**
   * ISO 4217 currency code for currency formatter.
   * @default 'USD'
   */
  currency?: string;

  /**
   * BCP 47 locale string for locale-aware formatters.
   * Used with: currency, number, date
   * @default 'en-US'
   */
  locale?: string;

  /**
   * Options for Intl.DateTimeFormat when using date type.
   *
   * @example
   * { "month": "short", "day": "numeric" } // "Jan 15"
   * { "year": "numeric", "month": "long" } // "January 2024"
   */
  dateOptions?: Intl.DateTimeFormatOptions;
}

/**
 * Configuration options for violin plot display.
 * Controls which summary statistics are shown in the violin box overlay.
 * Sent from the Python backend alongside violin_kde and violin_box layers.
 */
export interface ViolinOptions {
  /** Show median line marker. Default: true */
  showMedian?: boolean;
  /** Show mean value marker. Default: false */
  showMean?: boolean;
  /** Show extrema (min/max) markers. Default: true */
  showExtrema?: boolean;
}

/**
 * Data point for violin KDE (kernel density estimation) curves.
 * Library-agnostic — no SVG coordinates embedded in data.
 * The density field falls back to width if absent.
 */
export interface ViolinKdePoint {
  /** Categorical label for the violin (e.g., "setosa") */
  x: string | number;
  /** Position along the density axis */
  y: number;
  /** KDE density value at this point. Falls back to `width` if absent. */
  density?: number;
  /** Half-width of the violin at this Y level (used as density fallback) */
  width?: number;
  /** SVG viewport x-coordinate for highlight positioning (provided by backend) */
  svg_x?: number;
  /** SVG viewport y-coordinate for highlight positioning (provided by backend) */
  svg_y?: number;
}

/**
 * Root MAIDR data structure containing figure metadata and subplot grid.
 * This is the type for the `data` prop passed to the `<Maidr>` React component.
 *
 * @example
 * ```typescript
 * const data: Maidr = {
 *   id: 'my-chart',
 *   title: 'Sales by Quarter',
 *   subplots: [[{
 *     layers: [{
 *       id: '0',
 *       type: 'bar',
 *       axes: { x: 'Quarter', y: 'Revenue' },
 *       data: [{ x: 'Q1', y: 120 }, { x: 'Q2', y: 200 }],
 *     }],
 *   }]],
 * };
 * ```
 */
/**
 * Callback invoked when the active data point changes during navigation.
 * Used by canvas-based charting libraries (e.g., Chart.js) for visual highlighting.
 *
 * @param info - Object containing the current navigation position
 * @param info.layerId - The ID of the active layer/trace
 * @param info.row - The current row index (e.g., dataset index)
 * @param info.col - The current column index (e.g., data point index)
 */
export type NavigateCallback = (info: { layerId: string; row: number; col: number }) => void;

export interface Maidr {
  /** Unique identifier for the chart. Used for DOM element IDs. */
  id: string;
  /** Chart title displayed in text descriptions. */
  title?: string;
  /** Chart subtitle. */
  subtitle?: string;
  /** Chart caption. */
  caption?: string;
  /**
   * Optional figure-wide axis labels shared across every subplot — e.g. a facet
   * grid whose panels all sit on one common X and Y axis drawn at the figure
   * margins. Only `label` is honored at the figure level, so the type is
   * narrowed to `Pick<AxisConfig, 'label'>` (a layer's `min` / `max` /
   * `tickStep` / `format` have no figure-wide meaning and would be silently
   * ignored — the narrower type surfaces that as a compile error instead).
   *
   * When present and authored, the figure lobby's `l x` / `l y` announce these
   * as the figure-wide label; when omitted they fall back to the focused
   * subplot's own axis, so existing charts are unaffected.
   *
   * @example
   * axes: { x: { label: "Year" }, y: { label: "Revenue" } }
   */
  axes?: {
    x?: Pick<AxisConfig, 'label'>;
    y?: Pick<AxisConfig, 'label'>;
  };
  /**
   * 2D grid of subplots. Each row is an array of subplots.
   * For a single chart, use `[[{ layers: [...] }]]`.
   */
  subplots: MaidrSubplot[][];
  /**
   * Enables live/realtime mode for this chart. When true:
   * - React consumers can update the `data` prop to replace the chart data in place.
   * - Script-tag consumers can push updates via `window.maidrLive.setData()` /
   *   `window.maidrLive.appendData()`.
   * - The 'M' key toggles monitor mode, which auto-sonifies and announces
   *   newly appended data points.
   *
   * Static charts (the default) are unaffected.
   */
  live?: boolean;
  /**
   * Sliding window size for streaming data. When set, appending a data point
   * beyond this width drops the oldest point(s), keeping at most `maxWidth`
   * points per series. Only applies to `appendData` updates.
   */
  maxWidth?: number;
  /**
   * Optional callback invoked when the active data point changes.
   * Used by canvas-based charting libraries (e.g., Chart.js) for visual highlighting,
   * since canvas elements cannot be targeted with CSS selectors.
   *
   * This field is not serializable as JSON; it is only available when constructing
   * MAIDR data programmatically (e.g., via the Chart.js plugin or React API).
   */
  onNavigate?: NavigateCallback;
}

/**
 * Subplot data structure containing optional legend and trace layers.
 * A subplot groups one or more layers (traces) that share the same coordinate space.
 *
 * @example
 * ```typescript
 * const subplot: MaidrSubplot = {
 *   layers: [
 *     { id: '0', type: 'bar', axes: { x: 'X', y: 'Y' }, data: [...] },
 *     { id: '1', type: 'line', axes: { x: 'X', y: 'Y' }, data: [...] },
 *   ],
 * };
 * ```
 */
export interface MaidrSubplot {
  /** Legend labels for multi-series plots. */
  legend?: string[];
  /** CSS selector for the subplot container element. */
  selector?: string;
  /** Array of trace layers in this subplot. */
  layers: MaidrLayer[];
}

/**
 * Data point for bar charts with x and y coordinates.
 */
export interface BarPoint {
  x: string | number;
  y: number | string;
}

/**
 * Data point for boxplots containing quartiles, min/max, and outliers.
 */
export interface BoxPoint {
  z: string;
  lowerOutliers: number[];
  min: number;
  q1: number;
  q2: number;
  q3: number;
  max: number;
  upperOutliers: number[];
  /** Mean value for violin plots when mean display is enabled. */
  mean?: number;
}

/**
 * DOM selectors for boxplot visual elements.
 */
export interface BoxSelector {
  lowerOutliers: string[];
  min: string;
  iq: string;
  q2: string;
  max: string;
  upperOutliers: string[];
  /** CSS selector for mean marker element in violin plots. */
  mean?: string;
  /** Optional direct CSS selector for Q1 element (bypasses iq edge derivation). */
  q1?: string;
  /** Optional direct CSS selector for Q3 element (bypasses iq edge derivation). */
  q3?: string;
}

/**
 * Data point for candlestick charts with OHLC values, volume, and trend information.
 */
export interface CandlestickPoint {
  value: string;
  open: number;
  high: number;
  low: number;
  close: number;
  /** Optional volume data. May be undefined when source (e.g., Google Charts) doesn't provide it. */
  volume?: number;
  trend: CandlestickTrend;
  volatility: number;
}

/**
 * Data structure for heatmap charts with x/y labels and 2D point values.
 */
export interface HeatmapData {
  x: string[];
  y: string[];
  points: number[][];
}

/**
 * Data point for histograms extending bar points with bin ranges.
 */
export interface HistogramPoint extends BarPoint {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

/**
 * Data point for line charts with optional fill color for multi-series plots.
 */
export interface LinePoint {
  x: number | string;
  y: number;
  z?: string;
}

/**
 * Data point for scatter plots with x and y coordinates.
 */
export interface ScatterPoint {
  x: number;
  y: number;
}

/**
 * Data point for segmented/grouped bar charts with fill color identifier.
 */
export interface SegmentedPoint extends BarPoint {
  z: string;
}

/**
 * Data point for smooth/regression plots with data and SVG coordinate pairs.
 */
export interface SmoothPoint {
  x: number;
  y: number;
  svg_x: number;
  svg_y: number;
}

/**
 * Canonical axis configuration. Every axis (x, y, z) must be specified as an
 * object of this shape. The `label` is optional and falls back to built-in
 * defaults ('X', 'Y', 'Level') when omitted.
 *
 * Grid navigation properties (`min`, `max`, `tickStep`) are currently consumed
 * by scatter-plot traces only; they are silently ignored by other trace types.
 *
 * Formatting configuration lives inline as `format` on each axis, allowing
 * different formatters per axis without a separate top-level block.
 *
 * @example
 * // Simple label
 * axes: { x: { label: "Date" }, y: { label: "Price" } }
 *
 * @example
 * // With grid navigation (scatter)
 * axes: {
 *   x: { label: "Sepal Length", min: 4.3, max: 7.9, tickStep: 0.7 },
 *   y: { label: "Sepal Width",  min: 2,   max: 4.4, tickStep: 0.5 }
 * }
 *
 * @example
 * // With formatting
 * axes: {
 *   x: { label: "Date" },
 *   y: { label: "Price", format: { type: "currency", decimals: 2 } }
 * }
 */
export interface AxisConfig {
  /** Axis label displayed in text descriptions. Defaults applied when absent. */
  label?: string;
  /** Minimum value for grid navigation (scatter only). */
  min?: number;
  /** Maximum value for grid navigation (scatter only). */
  max?: number;
  /** Step size for grid navigation (scatter only). */
  tickStep?: number;
  /** Optional per-axis value formatting applied in text descriptions. */
  format?: AxisFormat;
}

/**
 * Chart orientation for bar and box plots.
 */
export enum Orientation {
  VERTICAL = 'vert',
  HORIZONTAL = 'horz',
}

/**
 * DOM selectors for candlestick chart visual elements.
 */
export interface CandlestickSelector {
  body: string | string[];
  wickHigh?: string | string[];
  wickLow?: string | string[];
  wick?: string | string[]; // single combined wick (high-to-low) line
  open?: string | string[];
  close?: string | string[];
}

/**
 * Layer/trace definition containing plot type, data, and rendering configuration.
 */
export interface MaidrLayer {
  id: string;
  type: TraceType;
  title?: string;
  selectors?: string | string[] | string[][] | BoxSelector[] | CandlestickSelector;
  orientation?: Orientation;
  /**
   * Optional DOM mapping hints. When provided, individual traces can opt-in
   * to use these hints to map DOM elements to the internal row-major data grid
   * without changing default behavior when omitted.
   */
  domMapping?: {
    /**
     * Specify DOM flattening order for grid-like traces.
     * 'row' => row-major, 'column' => column-major.
     */
    order?: 'row' | 'column';
    /**
     * For segmented/dodged bars, control the per-column group/level iteration.
     * 'forward' => iterate groups top-to-bottom (as previously domOrder='forward').
     * 'reverse' => iterate bottom-to-top (default).
     */
    groupDirection?: 'forward' | 'reverse';
    /**
     * For boxplots, control the Q1/Q3 edge mapping for IQR box.
     * 'forward' => Q1=bottom, Q3=top (default for vertical)
     * 'reverse' => Q1=top, Q3=bottom (for Base R vertical boxplots)
     */
    iqrDirection?: 'forward' | 'reverse';
  };
  /**
   * Axis configuration. Every axis (x, y, z) is specified as an {@link AxisConfig}
   * object with an optional `label`, optional grid navigation properties
   * (`min`, `max`, `tickStep`), and optional per-axis `format`.
   *
   * @example
   * // Basic labels
   * axes: { x: { label: "Date" }, y: { label: "Price" } }
   *
   * @example
   * // With per-axis formatting
   * axes: {
   *   x: { label: "Date" },
   *   y: { label: "Price", format: { type: "currency", decimals: 2 } }
   * }
   *
   * @example
   * // With grid navigation (scatter)
   * axes: {
   *   x: { label: "Sepal Length", min: 4.3, max: 7.9, tickStep: 0.7 },
   *   y: { label: "Sepal Width",  min: 2,   max: 4.4, tickStep: 0.5 }
   * }
   */
  axes?: {
    x?: AxisConfig;
    y?: AxisConfig;
    z?: AxisConfig;
  };
  /**
   * Optional display configuration for violin plot layers (VIOLIN_KDE and VIOLIN_BOX).
   * Controls which summary statistics are shown in the violin box overlay.
   */
  violinOptions?: ViolinOptions;
  data:
    | BarPoint[]
    | BoxPoint[]
    | CandlestickPoint[]
    | HeatmapData
    | HistogramPoint[]
    | LinePoint[][]
    | ScatterPoint[]
    | SegmentedPoint[][]
    | SmoothPoint[][]
    | ViolinKdePoint[][];
}

/**
 * Enumeration of supported plot trace types.
 * Use these values for the `type` field in {@link MaidrLayer}.
 *
 * @example
 * ```typescript
 * import { TraceType } from 'maidr/react';
 * const layer = { id: '0', type: TraceType.BAR, ... };
 * // Or use the string value directly:
 * const layer2 = { id: '0', type: 'bar', ... };
 * ```
 */
export enum TraceType {
  BAR = 'bar',
  BOX = 'box',
  CANDLESTICK = 'candlestick',
  /**
   * Virtual layer comparing candlestick OHLC fields against a reference
   * line (e.g. a moving average). Never declared in MAIDR JSON — created at
   * runtime by the candlestick delta feature (Alt+L to toggle, Ctrl+Shift+L
   * to pick the reference line).
   */
  CANDLESTICK_DELTA = 'candlestick_delta',
  DODGED = 'dodged_bar',
  HEATMAP = 'heat',
  HISTOGRAM = 'hist',
  LINE = 'line',
  NORMALIZED = 'stacked_normalized_bar',
  SCATTER = 'point',
  SMOOTH = 'smooth',
  STACKED = 'stacked_bar',
  VIOLIN_BOX = 'violin_box',
  VIOLIN_KDE = 'violin_kde',
}
